import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { haptics } from "@/lib/haptics";

const ROOT_PATHS = new Set([
  "/",
  "/analytics",
  "/devices",
  "/applications",
  "/getting-started",
]);

const EDGE_PX = 24;
const TRIGGER_PX = 80;

/**
 * iOS-style edge-pan-from-left to navigate back. No-op on root tabs and on
 * non-touch devices. Designed to coexist with horizontal scrollers — we only
 * start a back-gesture when the touch begins within the left edge zone.
 */
export function useSwipeBack(enabled = true) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (!("ontouchstart" in window)) return;
    if (ROOT_PATHS.has(location.pathname)) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      if (t.clientX > EDGE_PX) return;
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      // If user scrolls vertically, abort the back gesture.
      if (dy > 24 && dy > dx) tracking = false;
    };

    const onEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      if (dx > TRIGGER_PX && window.history.length > 1) {
        haptics.trigger("medium");
        navigate(-1);
      }
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [enabled, location.pathname, navigate]);
}

export default useSwipeBack;
